import { createContext, useContext } from "react";
import { SessionType } from "./Components/Session/Types";
import { TaskType } from "./Components/Task/Types";
import { GetSessions, SaveSessions } from "./Storage/LocalStorage";

type State = {
  sessions: SessionType[];
  currentSession: number;
};

export const selectSession = (state: State, id: number): SessionType => {
  const session = state.sessions.find((session) => session.id === id);
  if (session) {
    return session;
  }
  return state.sessions[0];
};

export const useSelectedSession = () => {
  const [state] = useAppState();
  return selectSession(state, state.currentSession);
};

export const selectTask = (state: State, id: number) => {
  const session = selectSession(state, state.currentSession);
  const task = session.tasks.find((task) => task.id === id);
  if (task) {
    return task;
  }
  return session.tasks[0];
};

export type Action =
  | { type: "SELECT_SESSION"; payload: { sessionId: number } }
  | { type: "DELETE_SESSION"; payload: { sessionId: number } }
  | {
      type: "UPDATE_TASKS_ORDER";
      payload: { sessionId: number; tasks: TaskType[] };
    }
  | { type: "DELETE_TASK"; payload: { sessionId: number; taskId: number } }
  | { type: "CREATE_SESSION"; payload: { session: SessionType } }
  | { type: "CREATE_TASK"; payload: { sessionId: number; task: TaskType } }
  | { type: "SET_SESSION_NAME"; payload: { sessionId: number; name: string } }
  | { type: "SET_SESSION_TIME"; payload: { sessionId: number; time: number } }
  | {
      type: "SET_SESSION_ACTIVE";
      payload: { sessionId: number; active: boolean };
    }
  | {
      type: "SET_TASK_TIME";
      payload: { sessionId: number; taskId: number; time: number };
    }
  | {
      type: "SET_TASK_ACTIVE";
      payload: { sessionId: number; taskId: number; active: boolean };
    }
  | {
      type: "SET_TASK_NAME";
      payload: { sessionId: number; taskId: number; name: string };
    }
  | {
      type: "SET_TASK_DESCRIPTION";
      payload: { sessionId: number; taskId: number; description: string };
    }
  | { type: "CLEAR_SESSIONS" };

const reducerForState = (state: State, action: Action) => {
  switch (action.type) {
    case "SET_SESSION_ACTIVE": {
      return {
        ...state,
        sessions: state.sessions.map((session) =>
          session.id === action.payload.sessionId
            ? { ...session, active: action.payload.active }
            : session
        ),
      };
    }
    case "SET_SESSION_NAME": {
      return {
        ...state,
        sessions: state.sessions.map((session) =>
          session.id === action.payload.sessionId
            ? { ...session, name: action.payload.name }
            : session
        ),
      };
    }
    case "SET_TASK_TIME": {
      return {
        ...state,
        sessions: state.sessions.map((session) =>
          session.id === action.payload.sessionId
            ? {
                ...session,
                tasks: session.tasks.map((task) =>
                  task.id === action.payload.taskId
                    ? { ...task, time: action.payload.time }
                    : task
                ),
              }
            : session
        ),
      };
    }
    case "SET_SESSION_TIME": {
      return {
        ...state,
        sessions: state.sessions.map((session) =>
          session.id === action.payload.sessionId
            ? { ...session, time: action.payload.time }
            : session
        ),
      };
    }
    case "SET_TASK_ACTIVE": {
      return {
        ...state,
        sessions: state.sessions.map((session) =>
          session.id === action.payload.sessionId
            ? {
                ...session,
                tasks: session.tasks.map((task) =>
                  task.id === action.payload.taskId
                    ? { ...task, active: action.payload.active }
                    : task
                ),
              }
            : session
        ),
      };
    }
    case "SET_TASK_NAME": {
      return {
        ...state,
        sessions: state.sessions.map((session) =>
          session.id === action.payload.sessionId
            ? {
                ...session,
                tasks: session.tasks.map((task) =>
                  task.id === action.payload.taskId
                    ? { ...task, name: action.payload.name }
                    : task
                ),
              }
            : session
        ),
      };
    }
    case "SET_TASK_DESCRIPTION": {
      return {
        ...state,
        sessions: state.sessions.map((session) =>
          session.id === action.payload.sessionId
            ? {
                ...session,
                tasks: session.tasks.map((task) =>
                  task.id === action.payload.taskId
                    ? { ...task, description: action.payload.description }
                    : task
                ),
              }
            : session
        ),
      };
    }
    // In your reducer function
    case "UPDATE_TASKS_ORDER": {
      const { sessionId, tasks } = action.payload;
      return {
        ...state,
        sessions: state.sessions.map((session) => {
          if (session.id === sessionId) {
            return {
              ...session,
              tasks: tasks,
            };
          }
          return session;
        }),
      };
    }
    case "SELECT_SESSION":
      return { ...state, currentSession: action.payload.sessionId };
    case "DELETE_SESSION": {
      const sessions = state.sessions.filter(
        (session) => session.id !== action.payload.sessionId
      );
      return {
        ...state,
        sessions,
        currentSession: sessions.length > 0 ? sessions[0].id : 0,
      };
    }
    case "DELETE_TASK": {
      const session = selectSession(state, action.payload.sessionId);
      const tasks = session.tasks.filter(
        (task) => task.id !== action.payload.taskId
      );
      return {
        ...state,
        sessions: state.sessions.map((session) =>
          session.id === action.payload.sessionId
            ? { ...session, tasks }
            : session
        ),
      };
    }
    case "CREATE_SESSION":
      return {
        ...state,
        sessions: [...state.sessions, action.payload.session],
      };
    case "CREATE_TASK": {
      return {
        ...state,
        sessions: state.sessions.map((session) =>
          session.id === action.payload.sessionId
            ? { ...session, tasks: [...session.tasks, action.payload.task] }
            : session
        ),
      };
    }
    case "CLEAR_SESSIONS":
      return initialState;
    default:
      return state;
  }
};
export const reducer = (state: State, action: Action) => {
  state = reducerForState(state, action);
  SaveSessions(state.sessions);
  return state;
};

export const initialState = {
  sessions:
    GetSessions().length > 0
      ? GetSessions()
      : [
          {
            id: 0,
            name: "Session 1",
            tasks: [],
            active: false,
          },
        ],
  currentSession: GetSessions().length > 0 ? GetSessions()[0].id : 0,
};

export const stateContext = createContext<[State, (action: Action) => void]>([
  initialState,
  () => {},
]);

export const selectRemainingTime = (state: State) => {
  const session = selectSession(state, state.currentSession);
  return session.tasks.reduce((acc, task) => acc + task.minutes, 0);
};

export const useAppState = () => {
  const context = useContext(stateContext);
  if (context === undefined) {
    throw new Error("useState must be used within a StateProvider");
  }
  return context;
};

export const useSelector = <T>(selector: (state: State) => T) => {
  const [state] = useAppState();
  return selector(state);
};
